import type { DraggableData } from "@/dnd/ids";
import { ChatsRailChatRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/ChatsRailChatRow";
import type { RailSelectionSource } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import type { InboxItem } from "@cohesive/api-client";
import { useDraggable } from "@dnd-kit/core";
import { useMemo } from "react";

function chatLabel(item: InboxItem) {
  return item.flow_session?.name?.trim() || "Untitled chat";
}

/** Draggable chat row — must render inside `TransferDndProvider`. */
export function ChatsRailDraggableChatRow({
  item,
  dragNamespace,
  active,
  section = "canonical",
  showActionsMenu = false,
  onOpen,
  onToggleStar,
  onRename,
  onDelete,
  expanded,
  onToggleExpanded,
  listItem = true,
}: {
  item: InboxItem;
  dragNamespace: string;
  active?: boolean;
  section?: RailSelectionSource;
  showActionsMenu?: boolean;
  onOpen: (item: InboxItem) => void;
  onToggleStar?: (item: InboxItem) => void;
  onRename: (item: InboxItem) => void;
  onDelete: (item: InboxItem) => void;
  /** Disclosure state for the chat's nested files (opt-in). */
  expanded?: boolean;
  onToggleExpanded?: () => void;
  listItem?: boolean;
}) {
  const label = chatLabel(item);

  const draggableData: DraggableData = useMemo(
    () => ({
      kind: "chat-session",
      payload: {
        flowSessionId: item.flow_session_id,
        sourceCanvasId: "",
        name: label,
        inboxItemId: item.id,
        sourceWorkspaceId: item.flow_session?.workspace_id,
        sourceFolderId: item.flow_session?.folder_id ?? null,
      },
    }),
    [
      item.flow_session_id,
      item.id,
      label,
      item.flow_session?.workspace_id,
      item.flow_session?.folder_id,
    ],
  );

  const draggable = useDraggable({
    id: `chats-rail-chat:${dragNamespace}:${item.id}`,
    data: draggableData,
  });

  return (
    <ChatsRailChatRow
      item={item}
      active={active}
      section={section}
      showActionsMenu={showActionsMenu}
      onOpen={onOpen}
      onToggleStar={onToggleStar}
      onRename={onRename}
      onDelete={onDelete}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      listItem={listItem}
      dragRef={draggable.setNodeRef}
      dragProps={{
        ...draggable.attributes,
        ...draggable.listeners,
      }}
      isDragging={draggable.isDragging}
    />
  );
}
